/**
 * `remove` — the verb whose ACTION depends on the target's state (D151).
 *
 * @remarks
 * `remove the mask` used to parse as a pick-up of the mask on your own face,
 * because `remove` was an alias of `take` in the base lexicon; moving the
 * alias to `unequip` would have broken `remove the panel`. One rule: `remove`
 * is `unequip` when the thing is worn and `take` otherwise — and it serves
 * every pack that ever declares a slot. Without a state (tooling narrows
 * without one), nothing is worn, so it is a take. Its own module because the
 * narrowing switch sits at the file-size cap.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { itemIdForEntityRef } from '../entities/entity-ref.js';

/** The single-object narrowing, borrowed for the object-less refusal so the two verbs refuse alike. */
type NarrowSingleObject = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	type: 'take',
) => CommandNarrowingOutcome;

/**
 * Narrow `remove <thing>` to `unequip` or `take` by whether the thing is worn.
 *
 * @param command - The parsed command
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param narrowSingleObject - The plain single-object narrowing, for the no-object refusal
 * @returns The narrowed action
 */
export const narrowRemoveCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	narrowSingleObject: NarrowSingleObject,
): CommandNarrowingOutcome => {
	if (command.directObjectRef === undefined) {
		return narrowSingleObject(command, bundle, state, 'take');
	}

	const itemId = itemIdForEntityRef(command.directObjectRef);
	const isWorn = state !== undefined && Object.values(state.world.equippedItemIds).includes(itemId);

	return { action: { itemId, type: isWorn ? 'unequip' : 'take' }, type: 'narrowed' };
};
